import * as React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

// How an order's Wolt courier is getting on. Absent on orders Wolt was never
// asked to carry, which is every order outside Budapest.
export type WoltStep = {
    label: string;
    at?: string | null;
    // True when `at` is a forecast rather than something that has happened.
    expected: boolean;
    status: 'DONE' | 'NEXT' | 'LATER' | 'FAILED';
    note?: string | null;
};

export type WoltExchange = {
    at?: string | null;
    type: string;
    message: string;
    request?: string | null;
    response?: string | null;
};

export type WoltDelivery = {
    state: 'PENDING' | 'BOOKED' | 'PICKUP_STARTED' | 'PICKED_UP' | 'DELIVERED'
        | 'CANCELLED' | 'CANCEL_FAILED' | 'REJECTED' | 'FAILED';
    needsAnotherDriver: boolean;
    trackingUrl?: string | null;
    problem?: string | null;
    now?: string | null;
    readyAt?: string | null;
    // Wolt's own forecasts, and beside them the arrival time the customer's
    // app is counting down to - so the two can be compared at a glance.
    pickupExpectedAt?: string | null;
    dropoffExpectedFrom?: string | null;
    dropoffExpectedTo?: string | null;
    customerSeesArrivalAt?: string | null;
    // Wolt's drop-off time has passed and Wolt never said "delivered".
    deliveryUnconfirmed?: boolean;
    canMarkDelivered?: boolean;
    steps?: WoltStep[] | null;
    log?: WoltExchange[] | null;
};

// The server sends UTC with a Z; shown in whatever time the dispatcher's own
// clock keeps, because that is the clock they are comparing it with.
const clock = (iso?: string | null, withSeconds = false): string => {
    if (!iso) return '';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        ...(withSeconds ? { second: '2-digit' } : {}),
        hour12: false,
    });
};

// A request is stored as its first line ("POST /v1/...") and then the JSON.
// Indented so a person can read it; left alone if it is not JSON after all.
const readable = (body?: string | null): string => {
    if (!body) return '';
    const newline = body.indexOf('\n');
    const head = newline > 0 && !body.trim().startsWith('{') ? body.slice(0, newline + 1) : '';
    const rest = head ? body.slice(newline + 1) : body;
    try {
        return head + JSON.stringify(JSON.parse(rest), null, 2);
    } catch {
        return body;
    }
};

// The times a dispatcher compares: when the kitchen finishes, when Wolt says
// its courier will collect and arrive, and what the customer is being told.
// A row is left out rather than shown empty when its time is not known yet.
const times = (delivery: WoltDelivery): Array<[string, string, boolean]> => {
    const rows: Array<[string, string, boolean]> = [];
    if (delivery.readyAt) {
        rows.push(['orders.wolt.foodReady', clock(delivery.readyAt), false]);
    }
    if (delivery.pickupExpectedAt) {
        rows.push(['orders.wolt.pickupExpected', clock(delivery.pickupExpectedAt), true]);
    }
    if (delivery.dropoffExpectedTo) {
        const from = clock(delivery.dropoffExpectedFrom);
        const to = clock(delivery.dropoffExpectedTo);
        rows.push(['orders.wolt.dropoffExpected', from && from !== to ? `${from} – ${to}` : to, true]);
    }
    if (delivery.customerSeesArrivalAt) {
        rows.push(['orders.wolt.customerSees', clock(delivery.customerSeesArrivalAt), false]);
    }
    return rows;
};

const MARK: Record<WoltStep['status'], { glyph: string; color: string }> = {
    DONE: { glyph: '✓', color: 'success.main' },
    NEXT: { glyph: '➜', color: 'primary.main' },
    LATER: { glyph: '○', color: 'text.disabled' },
    FAILED: { glyph: '✕', color: 'error.main' },
};

// How long past Wolt's latest drop-off time before the card raises it. The
// server uses the same figure; it is repeated here because the card has to
// notice the moment arriving on its own, with no new data to prompt it.
const UNCONFIRMED_AFTER_MS = 10 * 60 * 1000;
const LIVE_STATES: Array<WoltDelivery['state']> = ['BOOKED', 'PICKUP_STARTED', 'PICKED_UP'];

export const WoltTimeline: React.FC<{
    delivery: WoltDelivery;
    // Completes the order by hand when Wolt's own "delivered" never came.
    onMarkDelivered?: () => Promise<void>;
    // A finished order looked back on: whatever state Wolt's side was left
    // in, there is nothing to watch for and nothing to raise.
    historical?: boolean;
}> = ({ delivery, onMarkDelivered, historical = false }) => {
    const { t } = useTranslation();
    const steps = delivery.steps ?? [];
    const log = delivery.log ?? [];

    // The order list is only re-sent when something happens, and a deadline
    // passing is exactly nothing happening. A slow tick lets the card see it.
    const [, setTick] = React.useState(0);
    React.useEffect(() => {
        if (historical) return undefined;
        const timer = setInterval(() => setTick((tick) => tick + 1), 30000);
        return () => clearInterval(timer);
    }, [historical]);
    const [marking, setMarking] = React.useState(false);

    const dropoffBy = delivery.dropoffExpectedTo ? new Date(delivery.dropoffExpectedTo).getTime() : NaN;
    const unconfirmed = !historical
        && LIVE_STATES.includes(delivery.state)
        && (delivery.deliveryUnconfirmed === true
            || (!Number.isNaN(dropoffBy) && Date.now() > dropoffBy + UNCONFIRMED_AFTER_MS));
    const canMarkDelivered = !!onMarkDelivered
        && LIVE_STATES.includes(delivery.state)
        && (delivery.canMarkDelivered === true || unconfirmed);

    // Wolt refusing is not the end of the order: it is the moment a person has
    // to send somebody else, so that is the loud case and the rest are quiet.
    const alarming = delivery.needsAnotherDriver || delivery.state === 'CANCEL_FAILED' || unconfirmed;

    return (
        <Box
            sx={{
                mb: 1.5,
                p: 1.25,
                borderRadius: 1,
                border: '1px solid',
                borderColor: alarming ? 'error.main' : 'divider',
            }}
        >
            <Typography sx={{ fontSize: 'var(--text-xxs)', color: 'text.secondary', mb: 0.5 }}>
                {t('orders.wolt.title').toLocaleUpperCase()}
            </Typography>
            <Typography
                sx={{
                    fontSize: 'var(--text-xxs)',
                    fontWeight: 700,
                    color: alarming ? 'error.main' : 'text.primary',
                }}
            >
                {unconfirmed
                    ? t('orders.wolt.unconfirmed')
                    : delivery.now ?? t(`orders.wolt.${delivery.state}`)}
            </Typography>
            <Box sx={{ mt: 0.75, display: 'grid', gridTemplateColumns: '1fr auto', rowGap: 0.25, columnGap: 1 }}>
                {times(delivery).map(([label, value, strong]) => (
                    <React.Fragment key={label}>
                        <Typography sx={{ fontSize: 'var(--text-xxs)', color: 'text.secondary' }}>
                            {t(label)}
                        </Typography>
                        <Typography
                            sx={{ fontSize: 'var(--text-xxs)', fontWeight: strong ? 700 : 400, textAlign: 'right' }}
                        >
                            {value}
                        </Typography>
                    </React.Fragment>
                ))}
            </Box>

            <Box sx={{ mt: 1 }}>
                {steps.map((step, index) => (
                    <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', py: 0.35 }}>
                        <Typography
                            sx={{
                                fontSize: 'var(--text-xxs)',
                                fontWeight: 700,
                                width: 14,
                                textAlign: 'center',
                                color: MARK[step.status].color,
                            }}
                        >
                            {MARK[step.status].glyph}
                        </Typography>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                                sx={{
                                    fontSize: 'var(--text-xxs)',
                                    fontWeight: step.status === 'NEXT' || step.status === 'FAILED' ? 700 : 400,
                                    color: step.status === 'LATER'
                                        ? 'text.disabled'
                                        : step.status === 'FAILED' ? 'error.main' : 'text.primary',
                                }}
                            >
                                {step.status === 'NEXT' ? `${t('orders.wolt.next')}: ${step.label}` : step.label}
                            </Typography>
                            {step.note && (
                                <Typography
                                    sx={{
                                        fontSize: 'var(--text-xxs)',
                                        color: 'text.secondary',
                                        overflowWrap: 'anywhere',
                                    }}
                                >
                                    {step.note}
                                </Typography>
                            )}
                        </Box>
                        <Typography
                            sx={{
                                fontSize: 'var(--text-xxs)',
                                color: step.status === 'LATER' ? 'text.disabled' : 'text.secondary',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {step.at
                                ? step.expected ? t('orders.wolt.expected', { time: clock(step.at) }) : clock(step.at)
                                : ''}
                        </Typography>
                    </Box>
                ))}
            </Box>

            {delivery.trackingUrl && !delivery.needsAnotherDriver && (
                <Typography
                    component="a"
                    href={delivery.trackingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ fontSize: 'var(--text-xxs)', display: 'inline-block', mt: 0.75 }}
                >
                    {t('orders.wolt.track')}
                </Typography>
            )}

            {canMarkDelivered && (
                <Box sx={{ mt: 1 }}>
                    <Button
                        size="small"
                        variant={unconfirmed ? 'contained' : 'outlined'}
                        color={unconfirmed ? 'error' : 'primary'}
                        disabled={marking}
                        onClick={async () => {
                            // Completing an order pays out its credit and tells
                            // the customer it arrived, so it asks first.
                            if (!window.confirm(t('orders.wolt.markDeliveredConfirm'))) return;
                            setMarking(true);
                            try {
                                await onMarkDelivered!();
                            } finally {
                                setMarking(false);
                            }
                        }}
                        sx={{ fontSize: 'var(--text-xxs)', textTransform: 'none' }}
                    >
                        {t('orders.wolt.markDelivered')}
                    </Button>
                </Box>
            )}

            {log.length > 0 && (
                <Box component="details" sx={{ mt: 1 }}>
                    <Box
                        component="summary"
                        sx={{ fontSize: 'var(--text-xxs)', color: 'text.secondary', cursor: 'pointer' }}
                    >
                        {t('orders.wolt.log', { count: log.length })}
                    </Box>
                    {log.map((entry, index) => (
                        <Box
                            key={index}
                            component="details"
                            sx={{ mt: 0.75, pl: 1, borderLeft: '2px solid', borderColor: 'divider' }}
                        >
                            <Box
                                component="summary"
                                sx={{ fontSize: 'var(--text-xxs)', cursor: 'pointer', overflowWrap: 'anywhere' }}
                            >
                                <b>{clock(entry.at, true)}</b> {entry.message}
                            </Box>
                            {entry.request && (
                                <>
                                    <Typography sx={{ fontSize: 'var(--text-xxs)', color: 'text.secondary', mt: 0.5 }}>
                                        {t('orders.wolt.sent')}
                                    </Typography>
                                    <Box
                                        component="pre"
                                        sx={{
                                            fontSize: 11, m: 0, p: 1, borderRadius: 1, overflowX: 'auto',
                                            bgcolor: 'action.hover', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere',
                                        }}
                                    >
                                        {readable(entry.request)}
                                    </Box>
                                </>
                            )}
                            {entry.response && (
                                <>
                                    <Typography sx={{ fontSize: 'var(--text-xxs)', color: 'text.secondary', mt: 0.5 }}>
                                        {t('orders.wolt.received')}
                                    </Typography>
                                    <Box
                                        component="pre"
                                        sx={{
                                            fontSize: 11, m: 0, p: 1, borderRadius: 1, overflowX: 'auto',
                                            bgcolor: 'action.hover', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere',
                                        }}
                                    >
                                        {readable(entry.response)}
                                    </Box>
                                </>
                            )}
                            {!entry.request && !entry.response && (
                                <Typography sx={{ fontSize: 'var(--text-xxs)', color: 'text.secondary', mt: 0.5 }}>
                                    {t('orders.wolt.nothingSent')}
                                </Typography>
                            )}
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
};
